import { Separator } from '../components/ui/separator';
import { CheckCircle, Lightbulb, Bookmark, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCourse } from '../context/CourseContext';
import { cn } from '../lib/utils';

export default function ContentViewer({ subtopic, onComplete, onNext, isLast }) {
  const { isSubtopicComplete, isBookmarked, toggleBookmark } = useCourse();
  const completed = isSubtopicComplete(subtopic?.id);
  const bookmarked = isBookmarked(subtopic?.id);

  if (!subtopic) return null;

  const renderContent = (block, idx) => {
    switch (block.type) {
      case 'text':
        return <p key={idx} className="leading-relaxed text-muted-foreground mb-4">{block.value}</p>;
      case 'heading':
        return <h3 key={idx} className="font-heading font-semibold text-foreground text-base mt-6 mb-2">{block.value}</h3>;
      case 'list':
        return (
          <ul key={idx} className="space-y-2 mb-4">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-muted-foreground leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-sf-blue mt-2.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'note':
        return (
          <div key={idx} className="bg-sf-blue-light dark:bg-primary/10 border border-sf-blue/20 rounded-lg p-4 mb-4 flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-sf-blue flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground leading-relaxed">{block.value}</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="prose-content" data-testid={`content-viewer-${subtopic.id}`}>
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <span className="text-xs font-medium text-sf-blue bg-sf-blue-light dark:bg-primary/10 px-2.5 py-1 rounded-full uppercase tracking-wider">
          {subtopic.id}
        </span>
        {subtopic.reading_time && (
          <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-full flex items-center gap-1" data-testid={`reading-time-${subtopic.id}`}>
            <Clock className="h-3 w-3" /> {subtopic.reading_time} min read
          </span>
        )}
        {completed && (
          <span className="text-xs font-medium text-sf-success bg-emerald-50 dark:bg-emerald-950 px-2.5 py-1 rounded-full flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Completed
          </span>
        )}
      </div>

      <div className="flex items-start justify-between gap-4 mb-6">
        <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-foreground">
          {subtopic.title}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "flex-shrink-0 h-9 w-9 mt-1",
            bookmarked ? "text-sf-warning" : "text-muted-foreground hover:text-sf-warning"
          )}
          onClick={() => toggleBookmark(subtopic.id)}
          data-testid={`bookmark-btn-${subtopic.id}`}
        >
          <Bookmark className={cn("h-5 w-5", bookmarked && "fill-current")} />
        </Button>
      </div>

      <div className="max-w-3xl">
        {subtopic.content?.map((block, idx) => renderContent(block, idx))}
      </div>

      <Separator className="my-8" />

      <div className="flex items-center justify-between">
        {!completed ? (
          <Button
            onClick={() => onComplete(subtopic.id)}
            className="bg-sf-blue hover:bg-sf-blue-dark text-white"
            data-testid={`mark-complete-btn-${subtopic.id}`}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark as Complete
          </Button>
        ) : (
          <span className="text-sm text-sf-success font-medium flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4" />
            You've completed this topic
          </span>
        )}
        <Button
          variant="outline"
          onClick={onNext}
          data-testid="next-topic-btn"
        >
          {isLast ? 'Go to Quiz' : 'Next Topic'}
        </Button>
      </div>
    </div>
  );
}
